import { useState } from "react";
import { X, ChevronRight, ChevronLeft, Check, AlertTriangle, Upload, Image, Video } from "lucide-react";
import { MetaAdPreview } from "./MetaAdPreview";
import { TikTokAdPreview } from "./TikTokAdPreview";

interface CreateWizardProps {
  onClose: () => void;
  onSave: (draft: DraftCampaign) => void | Promise<void>;
}

export interface DraftCampaign {
  platforms: string[];
  name: string;
  objective: string;
  dailyBudget: number;
  headline: string;
  body: string;
  callToAction: string;
  landingPageUrl: string;
  creativeType: string;
  targetAgeMin: number;
  targetAgeMax: number;
  targetGender: string;
  targetLocations: string;
}

const OBJECTIVES = [
  { value: "AWARENESS", label: "Awareness", meta: "BRAND_AWARENESS", tiktok: "REACH" },
  { value: "TRAFFIC", label: "Traffic", meta: "LINK_CLICKS", tiktok: "TRAFFIC" },
  { value: "ENGAGEMENT", label: "Engagement", meta: "POST_ENGAGEMENT", tiktok: "ENGAGEMENT" },
  { value: "VIDEO_VIEWS", label: "Video Views", meta: "VIDEO_VIEWS", tiktok: "VIDEO_VIEWS" },
  { value: "LEADS", label: "Leads", meta: "LEAD_GENERATION", tiktok: "LEAD_GENERATION" },
  { value: "APP_INSTALLS", label: "App Installs", meta: "APP_INSTALLS", tiktok: "APP_PROMOTION" },
  { value: "CONVERSIONS", label: "Conversions", meta: "CONVERSIONS", tiktok: "CONVERSIONS" },
  { value: "CATALOG_SALES", label: "Catalog Sales", meta: "CATALOG_SALES", tiktok: "PRODUCT_SALES" },
];

const STEPS = ["Platforms", "Campaign", "Targeting", "Creative", "Preview"];

const ASPECT_RATIOS: Record<string, { label: string; meta: boolean; tiktok: boolean; rec: string }> = {
  "1:1": { label: "Square (1:1)", meta: true, tiktok: true, rec: "1080×1080" },
  "1.91:1": { label: "Landscape (1.91:1)", meta: true, tiktok: false, rec: "1200×628" },
  "4:5": { label: "Portrait (4:5)", meta: true, tiktok: false, rec: "1080×1350" },
  "9:16": { label: "Vertical (9:16)", meta: true, tiktok: true, rec: "1080×1920" },
};

export function CreateWizard({ onClose, onSave }: CreateWizardProps) {
  const [step, setStep] = useState(0);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(new Set(["meta"]));
  const [name, setName] = useState("");
  const [objective, setObjective] = useState("CONVERSIONS");
  const [dailyBudget, setDailyBudget] = useState(50);
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [callToAction, setCallToAction] = useState("SHOP_NOW");
  const [landingPageUrl, setLandingPageUrl] = useState("");
  const [creativeType, setCreativeType] = useState("image");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [targetAgeMin, setTargetAgeMin] = useState(18);
  const [targetAgeMax, setTargetAgeMax] = useState(65);
  const [targetGender, setTargetGender] = useState("all");
  const [targetLocations, setTargetLocations] = useState("United States");
  const [platformTab, setPlatformTab] = useState<"shared" | "meta" | "tiktok">("shared");
  const [saving, setSaving] = useState(false);

  const hasMeta = selectedPlatforms.has("meta");
  const hasTiktok = selectedPlatforms.has("tiktok");
  const multiPlatform = hasMeta && hasTiktok;

  const togglePlatform = (slug: string) => {
    const next = new Set(selectedPlatforms);
    if (next.has(slug)) { if (next.size > 1) next.delete(slug); }
    else next.add(slug);
    setSelectedPlatforms(next);
  };

  const canProceed = () => {
    if (step === 0) return selectedPlatforms.size > 0;
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return targetLocations.trim().length > 0;
    if (step === 3) return headline.trim().length > 0;
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        platforms: Array.from(selectedPlatforms), name, objective, dailyBudget,
        headline, body, callToAction, landingPageUrl, creativeType,
        targetAgeMin, targetAgeMax, targetGender, targetLocations,
      });
    } finally {
      setSaving(false);
    }
  };

  const obj = OBJECTIVES.find(o => o.value === objective);
  const currentAR = ASPECT_RATIOS[aspectRatio];
  const arWarnings: string[] = [];
  if (hasTiktok && creativeType === "image" && aspectRatio !== "9:16" && aspectRatio !== "1:1") {
    arWarnings.push("TikTok strongly recommends 9:16 vertical or 1:1 square for best performance.");
  }
  if (hasTiktok && creativeType === "image") {
    arWarnings.push("TikTok recommends video ads over static images for higher engagement.");
  }
  if (hasMeta && aspectRatio === "9:16" && creativeType === "image") {
    arWarnings.push("Meta will crop 9:16 images in Facebook Feed. Consider 1:1 or 4:5 for Feed placement.");
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="create-wizard-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-[760px] max-h-[85vh] flex flex-col" data-testid="create-wizard">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Create Campaign</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="button-close-wizard">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex items-center gap-1 px-6 py-3 border-b border-gray-100">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${i === step ? "bg-indigo-100 text-indigo-700" : i < step ? "text-green-600" : "text-gray-400"}`}>
                {i < step ? <Check className="w-3 h-3 inline" /> : null} {s}
              </span>
              {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Platforms</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { slug: "meta", name: "Meta", color: "#1877F2", desc: "Facebook & Instagram ads" },
                  { slug: "tiktok", name: "TikTok", color: "#000000", desc: "TikTok For Business ads" },
                ].map(p => {
                  const selected = selectedPlatforms.has(p.slug);
                  return (
                    <button key={p.slug} onClick={() => togglePlatform(p.slug)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${selected ? "border-indigo-500 bg-indigo-50" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`wizard-platform-${p.slug}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: p.color }}>
                          {p.slug === "meta" ? "M" : "T"}
                        </div>
                        <div>
                          <p className="font-semibold text-sm text-gray-900">{p.name}</p>
                          <p className="text-xs text-gray-500">{p.desc}</p>
                        </div>
                      </div>
                      {selected && <Check className="w-4 h-4 text-indigo-600 mt-2 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              {multiPlatform && (
                <div className="flex gap-1 border-b border-gray-200 mb-2">
                  {(["shared", "meta", "tiktok"] as const).map(t => (
                    <button key={t} onClick={() => setPlatformTab(t)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 ${platformTab === t ? "border-indigo-500 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                      data-testid={`tab-campaign-${t}`}>
                      {t === "shared" ? "Shared" : t === "meta" ? "Meta" : "TikTok"}
                    </button>
                  ))}
                </div>
              )}

              {(platformTab === "shared" || !multiPlatform) && (
                <>
                  <h3 className="text-sm font-semibold text-gray-900">Campaign Setup</h3>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Campaign Name</label>
                    <input value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g., Summer Sale 2026" data-testid="input-campaign-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Objective</label>
                    <select value={objective} onChange={e => setObjective(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="select-objective">
                      {OBJECTIVES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {obj && (
                      <div className="mt-2 flex gap-3 text-xs text-gray-500">
                        {hasMeta && <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">Meta: {obj.meta}</span>}
                        {hasTiktok && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">TikTok: {obj.tiktok}</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Daily Budget ($)</label>
                    <input type="number" value={dailyBudget} onChange={e => setDailyBudget(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" min={1} step={1} data-testid="input-budget" />
                  </div>
                </>
              )}

              {platformTab === "meta" && multiPlatform && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#1877F2] text-white text-[10px] font-bold flex items-center justify-center">M</span>
                    Meta Campaign Settings
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-800 mb-1">Campaign Budget Optimization (CBO)</p>
                    <p className="text-xs text-blue-600">Meta will automatically distribute budget across ad sets to maximize results. Budget is set at the campaign level.</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-800 mb-1">Special Ad Categories</p>
                    <p className="text-xs text-blue-600">Credit, employment, housing, and social issues ads require special category declaration on Meta.</p>
                  </div>
                </div>
              )}

              {platformTab === "tiktok" && multiPlatform && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-black text-white text-[10px] font-bold flex items-center justify-center">T</span>
                    TikTok Campaign Settings
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-800 mb-1">Budget Type</p>
                    <p className="text-xs text-gray-600">TikTok supports both daily and lifetime budgets. Minimum daily campaign budget is $50.</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-800 mb-1">Bid Strategy</p>
                    <p className="text-xs text-gray-600">Lowest Cost (auto-bid) is recommended for new campaigns. Manual bidding available for experienced advertisers.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {multiPlatform && (
                <div className="flex gap-1 border-b border-gray-200 mb-2">
                  {(["shared", "meta", "tiktok"] as const).map(t => (
                    <button key={t} onClick={() => setPlatformTab(t)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 ${platformTab === t ? "border-indigo-500 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                      data-testid={`tab-targeting-${t}`}>
                      {t === "shared" ? "Shared" : t === "meta" ? "Meta" : "TikTok"}
                    </button>
                  ))}
                </div>
              )}

              {(platformTab === "shared" || !multiPlatform) && (
                <>
                  <h3 className="text-sm font-semibold text-gray-900">Targeting</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Age Min</label>
                      <input type="number" value={targetAgeMin} onChange={e => setTargetAgeMin(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" min={13} max={65} data-testid="input-age-min" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Age Max</label>
                      <input type="number" value={targetAgeMax} onChange={e => setTargetAgeMax(Number(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" min={13} max={65} data-testid="input-age-max" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Gender</label>
                    <select value={targetGender} onChange={e => setTargetGender(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="select-gender">
                      <option value="all">All</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Locations</label>
                    <input value={targetLocations} onChange={e => setTargetLocations(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="United States, Canada" data-testid="input-locations" />
                  </div>
                </>
              )}

              {platformTab === "meta" && multiPlatform && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#1877F2] text-white text-[10px] font-bold flex items-center justify-center">M</span>
                    Meta Targeting
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-800 mb-1">Placements</p>
                    <p className="text-xs text-blue-600">Advantage+ (Facebook Feed, Instagram Feed, Stories, Reels, Audience Network)</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-800 mb-1">Detailed Targeting</p>
                    <p className="text-xs text-blue-600">Interest/behavior targeting inherited from shared settings. Use Meta Ads Manager for advanced Custom Audiences.</p>
                  </div>
                </div>
              )}

              {platformTab === "tiktok" && multiPlatform && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-black text-white text-[10px] font-bold flex items-center justify-center">T</span>
                    TikTok Targeting
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-800 mb-1">Placements</p>
                    <p className="text-xs text-gray-600">TikTok Feed, Pangle (auto-optimized by TikTok)</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-800 mb-1">Interest Categories</p>
                    <p className="text-xs text-gray-600">Auto-matched from shared targeting. TikTok optimizes delivery using its algorithm.</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-amber-800 mb-1">TikTok Note</p>
                    <p className="text-xs text-amber-600">TikTok minimum daily budget is $20/ad group. Budget will be adjusted if below threshold.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              {multiPlatform && (
                <div className="flex gap-1 border-b border-gray-200 mb-2">
                  {(["shared", "meta", "tiktok"] as const).map(t => (
                    <button key={t} onClick={() => setPlatformTab(t)}
                      className={`px-3 py-2 text-xs font-medium border-b-2 ${platformTab === t ? "border-indigo-500 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                      data-testid={`tab-creative-${t}`}>
                      {t === "shared" ? "Shared" : t === "meta" ? "Meta" : "TikTok"}
                    </button>
                  ))}
                </div>
              )}

              {(platformTab === "shared" || !multiPlatform) && (
                <>
                  <h3 className="text-sm font-semibold text-gray-900">Creative</h3>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Creative Type</label>
                    <div className="grid grid-cols-3 gap-2">
                      {(["image", "video", "carousel"] as const).map(t => (
                        <button key={t} onClick={() => setCreativeType(t)}
                          className={`p-3 rounded-lg border-2 text-center ${creativeType === t ? "border-indigo-500 bg-indigo-50" : "border-gray-200"}`}
                          data-testid={`button-type-${t}`}>
                          {t === "image" ? <Image className="w-5 h-5 mx-auto text-gray-600 mb-1" /> :
                           t === "video" ? <Video className="w-5 h-5 mx-auto text-gray-600 mb-1" /> :
                           <div className="flex justify-center gap-0.5 mb-1"><div className="w-3 h-5 rounded bg-gray-300" /><div className="w-3 h-5 rounded bg-gray-300" /><div className="w-3 h-5 rounded bg-gray-300" /></div>}
                          <span className="text-xs capitalize">{t}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Aspect Ratio</label>
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(ASPECT_RATIOS).map(([k, v]) => {
                        const supported = (hasMeta && v.meta) || (hasTiktok && v.tiktok);
                        return (
                          <button key={k} onClick={() => supported && setAspectRatio(k)}
                            className={`p-2 rounded-lg border text-left text-xs ${aspectRatio === k ? "border-indigo-500 bg-indigo-50" : "border-gray-200"} ${!supported ? "opacity-40 cursor-not-allowed" : ""}`}
                            disabled={!supported} data-testid={`button-ar-${k.replace(/[:/]/g, "-")}`}>
                            <span className="font-medium">{v.label}</span>
                            <span className="text-gray-400 ml-1">{v.rec}</span>
                            <div className="flex gap-1 mt-1">
                              {v.meta && <span className="text-[9px] px-1 rounded bg-blue-50 text-blue-500">Meta</span>}
                              {v.tiktok && <span className="text-[9px] px-1 rounded bg-gray-100 text-gray-600">TikTok</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Drop your creative here or click to upload</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Recommended: {currentAR?.rec || "1080×1080"} px
                      {creativeType === "video" && " · MP4, MOV · Max 500MB"}
                      {creativeType === "image" && " · JPG, PNG · Max 30MB"}
                    </p>
                    <input type="file" className="hidden" accept={creativeType === "video" ? "video/*" : "image/*"} data-testid="input-creative-upload" />
                  </div>
                  {arWarnings.length > 0 && (
                    <div className="space-y-1.5">
                      {arWarnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-2 bg-amber-50 rounded-lg p-2.5">
                          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-700">{w}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Headline</label>
                    <input value={headline} onChange={e => setHeadline(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Your attention-grabbing headline" data-testid="input-headline" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Primary Text / Body</label>
                    <textarea value={body} onChange={e => setBody(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={3} placeholder="Ad body text..." data-testid="input-body" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Call to Action</label>
                    <select value={callToAction} onChange={e => setCallToAction(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="select-cta">
                      <option value="SHOP_NOW">Shop Now</option>
                      <option value="LEARN_MORE">Learn More</option>
                      <option value="SIGN_UP">Sign Up</option>
                      <option value="DOWNLOAD">Download</option>
                      <option value="CONTACT_US">Contact Us</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Landing Page URL</label>
                    <input value={landingPageUrl} onChange={e => setLandingPageUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://..." data-testid="input-landing-url" />
                  </div>
                </>
              )}

              {platformTab === "meta" && multiPlatform && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-[#1877F2] text-white text-[10px] font-bold flex items-center justify-center">M</span>
                    Meta Creative Settings
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-800 mb-1">Placement Optimization</p>
                    <p className="text-xs text-blue-600">Meta will auto-crop your creative for different placements (Feed, Stories, Reels). 1:1 is safest for Feed; 9:16 is best for Stories/Reels.</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-blue-800 mb-1">Advantage+ Creative</p>
                    <p className="text-xs text-blue-600">Meta may apply automatic enhancements (brightness, cropping, text overlay) to improve performance.</p>
                  </div>
                </div>
              )}

              {platformTab === "tiktok" && multiPlatform && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-black text-white text-[10px] font-bold flex items-center justify-center">T</span>
                    TikTok Creative Settings
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-800 mb-1">Video Specs</p>
                    <p className="text-xs text-gray-600">Recommended: 9:16 vertical video, 9-15 seconds. MP4/MOV, up to 500MB. Audio required.</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-800 mb-1">Spark Ads</p>
                    <p className="text-xs text-gray-600">Boost existing organic posts as ads. Spark Ads typically see 30-40% higher engagement.</p>
                  </div>
                  {creativeType === "image" && (
                    <div className="bg-amber-50 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-700">Static images perform significantly lower on TikTok. Consider switching to video for better results.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Preview</h3>
              <div className="flex gap-6 justify-center flex-wrap">
                {hasMeta && (
                  <div>
                    <p className="text-xs font-medium text-center text-gray-500 mb-2">Meta (Facebook Feed)</p>
                    <MetaAdPreview headline={headline} body={body} callToAction={callToAction} imageUrl={null}
                      landingPageUrl={landingPageUrl || "https://example.com"} creativeType={creativeType}
                      aspectRatio={aspectRatio === "1.91:1" ? "landscape" : "square"} />
                  </div>
                )}
                {hasTiktok && (
                  <div>
                    <p className="text-xs font-medium text-center text-gray-500 mb-2">TikTok</p>
                    <TikTokAdPreview headline={headline} body={body} imageUrl={null} thumbnailUrl={null} creativeType={creativeType} />
                  </div>
                )}
              </div>
              <div className="mt-6 bg-gray-50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-700 mb-2">Campaign Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-gray-500">Name:</span><span className="font-medium">{name}</span>
                  <span className="text-gray-500">Platforms:</span><span className="font-medium">{Array.from(selectedPlatforms).join(", ")}</span>
                  <span className="text-gray-500">Objective:</span><span className="font-medium">{objective}</span>
                  <span className="text-gray-500">Budget:</span><span className="font-medium">${dailyBudget}/day</span>
                  <span className="text-gray-500">Creative:</span><span className="font-medium">{creativeType} ({aspectRatio})</span>
                  <span className="text-gray-500">Audience:</span><span className="font-medium">{targetGender}, {targetAgeMin}-{targetAgeMax}, {targetLocations}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
          <button onClick={() => step > 0 ? setStep(step - 1) : onClose()}
            className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-900" data-testid="button-wizard-back">
            <ChevronLeft className="w-4 h-4" /> {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => { setStep(step + 1); setPlatformTab("shared"); }}
              disabled={!canProceed()}
              className="flex items-center gap-1 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="button-wizard-next">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-1 px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              data-testid="button-wizard-publish">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save as Draft
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function RefreshCw({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}
